import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import {
  Activity, LogOut, Play, Pause, RotateCcw, Tv, Bike,
  Share2, BarChart3, ChevronDown, TrendingUp, TrendingDown,
  Minus, Zap, Target, Eye, Shield, ArrowUpRight, ArrowDownRight
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import ShareDemoModal from "@/components/ShareDemoModal";
import InsightCard from "@/components/InsightCard";

const RacingIntelligencePage = ({ user, token, onLogout }) => {
  const navigate = useNavigate();
  const [tracks, setTracks] = useState([]);
  const [races, setRaces] = useState([]);
  const [selectedTrack, setSelectedTrack] = useState(null);
  const [selectedRace, setSelectedRace] = useState(null);
  const [intelligence, setIntelligence] = useState(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [postRaceReview, setPostRaceReview] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const streamRef = useRef(null);
  const lastValidRef = useRef(null);
  const authHeaders = { Authorization: `Bearer ${token}` };

  // Fetch tracks
  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/tracks`, { headers: authHeaders });
        setTracks(res.data);
        if (res.data.length > 0) setSelectedTrack(res.data[0].code);
      } catch (e) { /* silent */ }
    };
    load();
  }, []);

  // Fetch races when track changes
  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/races/current`, { headers: authHeaders });
        setRaces(res.data);
        const trackRaces = res.data.filter(r => r.track_code === selectedTrack);
        if (trackRaces.length > 0) setSelectedRace(trackRaces[0].race_id);
      } catch (e) { /* silent */ }
    };
    if (selectedTrack) load();
  }, [selectedTrack]);

  // Stream intelligence — with cached fallback
  const fetchIntelligence = useCallback(async () => {
    if (!selectedRace) return;
    try {
      const res = await axios.get(`${API}/live/race/${selectedRace}`, { headers: authHeaders });
      setIntelligence(res.data);
      lastValidRef.current = res.data;
      // Auto-stop at finish — hold post-race data for review
      if (res.data.phase === "finish" && streamRef.current) {
        clearInterval(streamRef.current);
        streamRef.current = null;
        setIsStreaming(false);
      }
    } catch (e) {
      // Use cached last valid state if API fails — never show blank
      if (lastValidRef.current) setIntelligence(lastValidRef.current);
    }
  }, [selectedRace]);

  // Load initial snapshot (one fetch, no streaming)
  useEffect(() => { fetchIntelligence(); }, [fetchIntelligence]);

  const startStreaming = () => {
    if (isStreaming) return;
    setPostRaceReview(false);
    setIsStreaming(true);
    streamRef.current = setInterval(fetchIntelligence, 800);
  };

  const stopStreaming = () => {
    setIsStreaming(false);
    if (streamRef.current) clearInterval(streamRef.current);
    streamRef.current = null;
  };

  const resetRace = async () => {
    stopStreaming();
    setPostRaceReview(false);
    if (selectedRace) {
      try {
        await axios.post(`${API}/admin/reset?race_id=${selectedRace}`, {}, { headers: authHeaders });
        await fetchIntelligence();
      } catch (e) { /* silent */ }
    }
  };

  const enterPostRaceReview = () => {
    stopStreaming();
    setPostRaceReview(true);
  };

  useEffect(() => () => { if (streamRef.current) clearInterval(streamRef.current); }, []);

  const d = intelligence;

  return (
    <div className="min-h-screen bg-[#090a0d] text-white" data-testid="racing-intelligence-page">
      {/* Header */}
      <header className="bg-[#0d0f12]/95 backdrop-blur-sm border-b border-white/10 px-4 h-14 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/" className="flex items-center gap-2 text-white/40 hover:text-white transition-colors" data-testid="back-to-hub">
            <Activity className="w-4 h-4 text-[#F97316]" />
            <span className="font-heading text-sm font-bold tracking-wider text-white uppercase">Race Intelligence</span>
          </Link>

          <div className="flex items-center gap-2 ml-4">
            <Select value={selectedTrack || ""} onValueChange={setSelectedTrack}>
              <SelectTrigger className="w-[130px] h-8 bg-[#121212] border-white/20 text-white text-xs" data-testid="track-selector">
                <SelectValue placeholder="Track" />
              </SelectTrigger>
              <SelectContent className="bg-[#1a1d22] border-white/20">
                {tracks.map(t => <SelectItem key={t.code} value={t.code} className="text-white text-xs">{t.name}</SelectItem>)}
              </SelectContent>
            </Select>

            {d && (
              <div className="flex items-center gap-3 ml-3">
                <StateChip label="Race State" value={d.phase?.toUpperCase()} />
                <StateChip label="Pressure" value={d.race_state?.pressure_state} colour={d.race_state?.pressure_state === "High" ? "#EF4444" : "#F97316"} />
                <StateChip label="Bias" value={d.race_state?.bias_state} colour={d.race_state?.bias_state !== "Neutral" ? "#3B82F6" : undefined} />
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-1.5">
          <Button onClick={isStreaming ? stopStreaming : startStreaming} size="sm" className={isStreaming ? "bg-red-600 hover:bg-red-700 text-white h-8" : "bg-[#10B981] hover:bg-[#059669] text-white h-8"} data-testid={isStreaming ? "stop-btn" : "start-btn"}>
            {isStreaming ? <><Pause className="w-3 h-3 mr-1" /> Stop</> : <><Play className="w-3 h-3 mr-1" /> Start</>}
          </Button>
          <Button onClick={resetRace} variant="ghost" size="sm" className="text-white/40 hover:text-white h-8 px-2"><RotateCcw className="w-3 h-3" /></Button>
          <div className="w-px h-5 bg-white/10 mx-0.5" />
          <Link to="/broadcast"><Button variant="ghost" size="sm" className="text-white/40 hover:text-white h-8 px-2"><Tv className="w-3.5 h-3.5" /></Button></Link>
          <Link to="/cross-sport"><Button variant="ghost" size="sm" className="text-white/40 hover:text-white h-8 px-2"><Bike className="w-3.5 h-3.5" /></Button></Link>
          <div className="w-px h-5 bg-white/10 mx-0.5" />
          <Button onClick={() => setShowShareModal(true)} size="sm" className="bg-[#F97316] hover:bg-[#EA580C] text-white h-8" data-testid="share-demo-btn"><Share2 className="w-3 h-3 mr-1" /> Share</Button>
          <Link to="/analytics"><Button variant="ghost" size="sm" className="text-white/40 hover:text-white h-8 px-2"><BarChart3 className="w-3.5 h-3.5" /></Button></Link>
          <Button onClick={onLogout} variant="ghost" size="sm" className="text-white/30 hover:text-white h-8 px-2"><LogOut className="w-3.5 h-3.5" /></Button>
        </div>
      </header>

      {/* Always-On Signal Bar */}
      {d && (
        <div className="bg-[#0d0f12] border-b border-white/5 px-4 py-1.5 flex items-center justify-between" data-testid="signal-bar">
          <div className="flex items-center gap-4 text-xs font-mono">
            {d.most_efficient_runner && (
              <span className="text-[#10B981] flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-[#10B981] animate-pulse" />
                Efficiency Leader: {d.most_efficient_runner}
              </span>
            )}
            {d.under_pressure_runner && (
              <span className="text-[#EF4444] flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-[#EF4444] animate-pulse" />
                Pressure: {d.under_pressure_runner}
              </span>
            )}
            {d.emerging_threat && (
              <span className="text-[#F59E0B] flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-[#F59E0B] animate-pulse" />
                Emerging: {d.emerging_threat}
              </span>
            )}
          </div>
          <span className="text-white/20 text-[10px] font-mono uppercase tracking-widest">
            {d.phase === "finish" ? "Race Complete" : "Live Intelligence"}
          </span>
        </div>
      )}

      {/* Main Content */}
      <main className="p-4 space-y-3 max-w-[1600px] mx-auto">
        {/* Terminal State Overlay — Race Complete */}
        {d && d.phase === "finish" && !postRaceReview && (
          <div className="rounded-xl border-2 border-[#F97316]/40 bg-[#F97316]/5 p-5 mb-3 animate-in fade-in" data-testid="race-complete-overlay">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-[#F97316]" />
                <span className="text-[#F97316] font-heading font-bold text-sm uppercase tracking-wider">Race Complete</span>
              </div>
              <div className="flex items-center gap-2">
                <Button onClick={enterPostRaceReview} size="sm" className="bg-[#F97316] hover:bg-[#EA580C] text-white h-8" data-testid="view-post-race-btn">
                  <Eye className="w-3 h-3 mr-1.5" /> View Post-Race Review
                </Button>
                <Button onClick={resetRace} variant="ghost" size="sm" className="text-white/40 hover:text-white h-8" data-testid="run-again-btn">
                  <RotateCcw className="w-3 h-3 mr-1.5" /> Run Again
                </Button>
              </div>
            </div>
            <p className="text-white/70 text-sm font-mono" data-testid="finish-what-mattered">{d.what_mattered}</p>
          </div>
        )}

        {/* Post-Race Review Mode — frozen, full breakdown */}
        {d && postRaceReview && (
          <div className="space-y-4 mb-3 animate-in fade-in" data-testid="post-race-review">
            <div className="rounded-xl border-2 border-[#10B981]/30 bg-[#10B981]/5 p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-[#10B981]" />
                  <span className="text-[#10B981] font-heading font-bold text-sm uppercase tracking-wider">Post-Race Review</span>
                </div>
                <div className="flex items-center gap-2">
                  <Button onClick={resetRace} size="sm" className="bg-[#10B981] hover:bg-[#059669] text-white h-8" data-testid="new-race-btn">
                    <RotateCcw className="w-3 h-3 mr-1.5" /> New Race
                  </Button>
                  <Link to="/">
                    <Button variant="ghost" size="sm" className="text-white/40 hover:text-white h-8" data-testid="review-back-hub">
                      Back to Hub
                    </Button>
                  </Link>
                </div>
              </div>

              {/* What Mattered */}
              <div className="p-4 rounded-xl border border-[#F97316]/30 bg-[#F97316]/5 mb-4">
                <div className="text-[#F97316] text-[10px] uppercase tracking-widest font-bold mb-1">What Mattered</div>
                <p className="text-white text-sm leading-relaxed">{d.what_mattered}</p>
              </div>

              {/* Post-race cards — full width for readability */}
              <div className="grid grid-cols-3 gap-3 mb-4">
                {d.cards?.best_run && (
                  <div className="bg-[#10B981]/10 border border-[#10B981]/20 rounded-lg p-3" data-testid="review-best-run">
                    <div className="text-[#10B981] text-[10px] font-bold uppercase tracking-wider mb-1">Best Run</div>
                    <div className="text-white text-sm font-bold">#{d.cards.best_run.saddle_number} {d.cards.best_run.horse_name}</div>
                    <div className="text-white/40 text-xs mt-1">{d.cards.best_run.verdict}</div>
                    <div className="text-white/30 text-[10px] mt-1">Next: <NextBadge value={d.cards.best_run.next_start} small /></div>
                  </div>
                )}
                {d.cards?.hidden_run && (
                  <div className="bg-[#3B82F6]/10 border border-[#3B82F6]/20 rounded-lg p-3" data-testid="review-hidden-run">
                    <div className="text-[#3B82F6] text-[10px] font-bold uppercase tracking-wider mb-1">Hidden Run</div>
                    <div className="text-white text-sm font-bold">#{d.cards.hidden_run.saddle_number} {d.cards.hidden_run.horse_name}</div>
                    <div className="text-white/40 text-xs mt-1">{d.cards.hidden_run.verdict}</div>
                    <div className="text-white/30 text-[10px] mt-1">Next: <NextBadge value={d.cards.hidden_run.next_start} small /></div>
                  </div>
                )}
                {d.cards?.forgive_run && (
                  <div className="bg-[#F59E0B]/10 border border-[#F59E0B]/20 rounded-lg p-3" data-testid="review-forgive-run">
                    <div className="text-[#F59E0B] text-[10px] font-bold uppercase tracking-wider mb-1">Forgive Run</div>
                    <div className="text-white text-sm font-bold">#{d.cards.forgive_run.saddle_number} {d.cards.forgive_run.horse_name}</div>
                    <div className="text-white/40 text-xs mt-1">{d.cards.forgive_run.verdict}</div>
                    <div className="text-white/30 text-[10px] mt-1">Next: <NextBadge value={d.cards.forgive_run.next_start} small /></div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {d ? (
          <>
            {/* Race Progress Bar */}
            <div className="flex items-center gap-3" data-testid="race-progress">
              <PhaseIndicator phase={d.phase} />
              <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-[#F97316] to-[#EF4444] transition-all duration-700"
                  style={{ width: `${d.distance_m > 0 ? Math.max(2, ((d.distance_m - d.distance_remaining_m) / d.distance_m) * 100) : 0}%` }}
                />
              </div>
              <span className="text-white/40 text-xs font-mono w-20 text-right">
                {d.distance_remaining_m > 0 ? `${Math.round(d.distance_remaining_m)}m to go` : "FINISHED"}
              </span>
            </div>

            {/* Race Map */}
            <div className="rounded-xl border border-white/10 bg-[#0d0f12]/80 overflow-hidden h-24" data-testid="race-map">
              <svg viewBox="0 0 1000 80" className="w-full h-full" preserveAspectRatio="none">
                {/* Track */}
                <rect x="40" y="15" width="920" height="50" rx="4" fill="rgba(255,255,255,0.02)" />
                <line x1="40" y1="40" x2="960" y2="40" stroke="rgba(255,255,255,0.05)" strokeDasharray="15 8" />
                {/* Finish line */}
                <line x1="950" y1="15" x2="950" y2="65" stroke="rgba(255,255,255,0.15)" strokeWidth="2" strokeDasharray="3 3" />
                <text x="955" y="42" fill="rgba(255,255,255,0.1)" fontSize="8" fontFamily="monospace">FIN</text>
                {/* Runners */}
                {d.field?.map((h, i) => {
                  const progress = d.distance_m > 0 ? (d.distance_m - d.distance_remaining_m + (h.x || 0)) / d.distance_m : 0;
                  const px = 50 + Math.min(0.95, Math.max(0, progress)) * 890;
                  const py = 22 + (i / Math.max(d.field.length - 1, 1)) * 38;
                  const isBest = d.cards?.best_run?.saddle_number === h.no;
                  return (
                    <g key={h.no}>
                      <circle cx={px} cy={py} r={isBest ? 5 : 3.5}
                        fill={h.eff === "Strong" ? "#10B981" : h.eff === "Weak" ? "#EF4444" : "#F59E0B"}
                        opacity={0.9} />
                      <text x={px + 7} y={py + 3} fill="rgba(255,255,255,0.5)" fontSize="7" fontFamily="monospace">{h.no}</text>
                    </g>
                  );
                })}
              </svg>
            </div>

            {/* What Mattered */}
            <div className="p-4 rounded-xl border border-[#F97316]/30 bg-[#F97316]/5" data-testid="what-mattered">
              <div className="text-[#F97316] text-[10px] uppercase tracking-widest font-bold mb-1">What Mattered</div>
              <p className="text-white text-sm leading-relaxed">{d.what_mattered}</p>
            </div>

            {/* Top 5 Intelligence Cards */}
            <div className="grid grid-cols-5 gap-3" data-testid="intelligence-cards">
              <IntelCard title="Best Run" icon={Trophy} data={d.cards?.best_run} colour="#10B981" assist={d.field?.find(h => h.no === d.cards?.best_run?.saddle_number)?.assist} />
              <IntelCard title="Hidden Run" icon={Eye} data={d.cards?.hidden_run} colour="#3B82F6" assist={d.field?.find(h => h.no === d.cards?.hidden_run?.saddle_number)?.assist} />
              <IntelCard title="Forgive Run" icon={Shield} data={d.cards?.forgive_run} colour="#A855F7" assist={d.field?.find(h => h.no === d.cards?.forgive_run?.saddle_number)?.assist} />
              <IntelCard title="Best Closer" icon={TrendingUp} data={d.cards?.best_closer} colour="#F59E0B" assist={d.field?.find(h => h.no === d.cards?.best_closer?.saddle_number)?.assist} />
              <IntelCard title="Best On Pace" icon={Zap} data={d.cards?.best_on_pace} colour="#EF4444" assist={d.field?.find(h => h.no === d.cards?.best_on_pace?.saddle_number)?.assist} />
            </div>

            {/* Field Intelligence Table */}
            <div className="rounded-xl border border-white/10 bg-[#0d0f12]/80 overflow-hidden" data-testid="field-table">
              <div className="px-4 py-2.5 border-b border-white/10 flex items-center justify-between">
                <span className="text-white/60 text-xs uppercase tracking-wider font-bold">Full Field Intelligence</span>
                <span className="text-white/30 text-xs">{d.field?.length} runners</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/10 text-white/40 text-[10px] uppercase tracking-wider">
                      <th className="px-3 py-2 text-left w-10">No</th>
                      <th className="px-3 py-2 text-left">Horse</th>
                      <th className="px-3 py-2 text-center w-12">Fin</th>
                      <th className="px-3 py-2 text-center w-12">True</th>
                      <th className="px-3 py-2 text-center w-16">Eff</th>
                      <th className="px-3 py-2 text-center w-14">Press</th>
                      <th className="px-3 py-2 text-center w-16">Path</th>
                      <th className="px-3 py-2 text-center w-20">Mom</th>
                      <th className="px-3 py-2 text-left min-w-[200px]">Verdict</th>
                      <th className="px-3 py-2 text-center w-20">Next</th>
                    <th className="px-3 py-2 text-center w-14">IAL</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.field?.map((h, i) => (
                      <tr key={h.no} className={`border-b border-white/5 ${i % 2 === 0 ? 'bg-white/[0.01]' : ''} hover:bg-white/[0.03] transition-colors`}>
                        <td className="px-3 py-2 text-white/60 font-mono text-xs">{h.no}</td>
                        <td className="px-3 py-2 text-white font-semibold text-xs">{h.horse}</td>
                        <td className="px-3 py-2 text-center text-white font-mono text-xs">{h.fin}</td>
                        <td className="px-3 py-2 text-center font-mono text-xs">
                          <span className={h.true < h.fin ? 'text-[#10B981] font-bold' : h.true > h.fin ? 'text-[#EF4444]' : 'text-white/60'}>{h.true}</span>
                        </td>
                        <td className="px-3 py-2 text-center"><BandBadge value={h.eff} type="eff" /></td>
                        <td className="px-3 py-2 text-center"><BandBadge value={h.press} type="press" /></td>
                        <td className="px-3 py-2 text-center"><BandBadge value={h.path} type="path" /></td>
                        <td className="px-3 py-2 text-center"><BandBadge value={h.mom} type="mom" /></td>
                        <td className="px-3 py-2 text-white/60 text-xs leading-relaxed max-w-[300px]">{h.verdict}</td>
                        <td className="px-3 py-2 text-center"><NextBadge value={h.next} /></td>
                        <td className="px-3 py-2 text-center"><InsightCard assist={h.assist} compact /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Bottom Row: Market Intelligence + Next-Start Signals */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {/* Market Intelligence */}
              <div className="rounded-xl border border-white/10 bg-[#0d0f12]/80 p-4" data-testid="market-intelligence">
                <div className="text-white/60 text-[10px] uppercase tracking-wider font-bold mb-3">Market vs Intelligence</div>
                <div className="space-y-1.5">
                  {d.field?.filter(h => h.market_gap !== 0).sort((a, b) => Math.abs(b.market_gap) - Math.abs(a.market_gap)).slice(0, 6).map(h => (
                    <div key={h.no} className="flex items-center justify-between py-1.5 px-2 rounded-lg bg-white/[0.02]">
                      <div className="flex items-center gap-2">
                        <span className="text-white/40 font-mono text-xs w-5">{h.no}</span>
                        <span className="text-white text-xs font-semibold">{h.horse}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-white/30 text-[10px]">Mkt {h.market_rank}</span>
                        <span className="text-white/30 text-[10px]">True {h.true}</span>
                        <span className={`text-xs font-bold ${h.market_gap > 0 ? 'text-[#10B981]' : h.market_gap < 0 ? 'text-[#EF4444]' : 'text-white/30'}`}>
                          {h.market_gap > 0 ? '+' : ''}{h.market_gap}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Next-Start Signals */}
              <div className="rounded-xl border border-white/10 bg-[#0d0f12]/80 p-4" data-testid="next-start-signals">
                <div className="text-white/60 text-[10px] uppercase tracking-wider font-bold mb-3">Next-Start Signals</div>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <div className="text-[#10B981] text-[10px] uppercase tracking-wider font-bold mb-2 flex items-center gap-1"><ArrowUpRight className="w-3 h-3" /> Upgrade</div>
                    {d.field?.filter(h => h.next === "Upgrade").map(h => (
                      <div key={h.no} className="text-white text-xs mb-1">#{h.no} {h.horse}</div>
                    ))}
                    {!d.field?.some(h => h.next === "Upgrade") && <div className="text-white/20 text-xs">None</div>}
                  </div>
                  <div>
                    <div className="text-white/40 text-[10px] uppercase tracking-wider font-bold mb-2 flex items-center gap-1"><Minus className="w-3 h-3" /> Neutral</div>
                    {d.field?.filter(h => h.next === "Neutral").slice(0, 4).map(h => (
                      <div key={h.no} className="text-white/50 text-xs mb-1">#{h.no} {h.horse}</div>
                    ))}
                  </div>
                  <div>
                    <div className="text-[#EF4444] text-[10px] uppercase tracking-wider font-bold mb-2 flex items-center gap-1"><ArrowDownRight className="w-3 h-3" /> Downgrade</div>
                    {d.field?.filter(h => h.next === "Downgrade").map(h => (
                      <div key={h.no} className="text-white text-xs mb-1">#{h.no} {h.horse}</div>
                    ))}
                    {!d.field?.some(h => h.next === "Downgrade") && <div className="text-white/20 text-xs">None</div>}
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="flex items-center justify-center h-[60vh]">
            <div className="text-center text-white/20">
              <Activity className="w-16 h-16 mx-auto mb-4 opacity-30" />
              <p className="font-heading text-lg">Loading Intelligence...</p>
            </div>
          </div>
        )}
      </main>

      {showShareModal && <ShareDemoModal token={token} onClose={() => setShowShareModal(false)} />}
    </div>
  );
};

// =============================================================================
// SUB-COMPONENTS
// =============================================================================

const Trophy = ({ className }) => <TrendingUp className={className} />;

const PHASE_COLOURS = { pre: "#666", early: "#F59E0B", mid: "#F97316", late: "#EF4444", finish: "#10B981" };
const PhaseIndicator = ({ phase }) => (
  <span className="px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-wider" style={{ backgroundColor: `${PHASE_COLOURS[phase] || '#666'}20`, color: PHASE_COLOURS[phase] || '#666' }}>
    {phase || "—"}
  </span>
);

const StateChip = ({ label, value, colour }) => (
  <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-white/5 border border-white/10">
    <span className="text-white/30 text-[9px] uppercase">{label}</span>
    <span className="text-xs font-bold" style={{ color: colour || '#FFF' }}>{value || "—"}</span>
  </div>
);

const IntelCard = ({ title, icon: Icon, data, colour, assist }) => (
  <div className="p-3 rounded-xl border border-white/10 bg-[#0d0f12]/80" data-testid={`card-${title.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="flex items-center gap-1.5 mb-2">
      <Icon className="w-3.5 h-3.5" style={{ color: colour }} />
      <span className="text-[10px] uppercase tracking-wider font-bold" style={{ color: colour }}>{title}</span>
    </div>
    {data ? (
      <>
        <div className="text-white font-bold text-sm mb-0.5">#{data.saddle_number} {data.horse_name}</div>
        <div className="text-white/40 text-[10px]">
          Fin {data.visible_position} · True {data.true_rank} · {data.efficiency_band} · {data.momentum_band}
        </div>
        <div className="text-white/30 text-[10px] mt-1 flex items-center gap-1">
          Next: <NextBadge value={data.next_start} small />
        </div>
        {assist && <InsightCard assist={assist} />}
      </>
    ) : (
      <div className="text-white/20 text-xs">Not identified</div>
    )}
  </div>
);

const BAND_COLOURS = {
  eff: { Strong: "#10B981", Neutral: "#F59E0B", Weak: "#EF4444" },
  press: { Low: "#10B981", Med: "#F59E0B", High: "#EF4444" },
  path: { Low: "#10B981", Moderate: "#F59E0B", High: "#EF4444" },
  mom: { Building: "#10B981", Sustained: "#F59E0B", "Peaked Early": "#EF4444", Faded: "#EF4444" },
};

const BandBadge = ({ value, type }) => {
  const colour = BAND_COLOURS[type]?.[value] || "#666";
  return (
    <span className="px-1.5 py-0.5 rounded text-[10px] font-bold" style={{ backgroundColor: `${colour}15`, color: colour }}>
      {value}
    </span>
  );
};

const NextBadge = ({ value, small }) => {
  const colour = value === "Upgrade" ? "#10B981" : value === "Downgrade" ? "#EF4444" : "#666";
  return (
    <span className={`px-1.5 py-0.5 rounded font-bold ${small ? 'text-[9px]' : 'text-[10px]'}`} style={{ backgroundColor: `${colour}15`, color: colour }}>
      {value}
    </span>
  );
};

export default RacingIntelligencePage;
